'use client';

import { AriaAttributes, FC, ReactNode } from 'react';

import classNames from 'classnames';

import dataAttributes from '../../../../utils/attributes/attributes';
import { Badge } from '../../../badge/components/Badge/Badge';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

interface Props {
   id?: string;
   variant?: 'primary' | 'secondary' | 'tertiary' | 'error' | 'ghost';
   leadingIcon?: string;
   trailingIcon?: string;
   badge?: string;
   disabled?: boolean;
   type?: 'button' | 'submit' | 'reset';
   form?: string;
   fullWidth?: boolean;
   ariaLabel?: AriaAttributes['aria-label'];
   attributes?: Record<string, string>;
   onClick?: () => void;
   children: ReactNode;
}

const badgeVariants = {
   primary: 'secondary',
   secondary: 'primary',
   tertiary: 'primary',
   quaternary: 'inverse-primary',
   error: 'secondary',
   ghost: 'inverse-primary'
} as const;

const backgroundColors = {
   primary:
      'bg-component-input-button-container-color-background-primary-enabled disabled:bg-component-input-button-container-color-background-primary-disabled hover:bg-component-input-button-container-color-background-primary-hover focus:bg-component-input-button-container-color-background-primary-focused active:bg-component-input-button-container-color-background-primary-pressed',
   secondary:
      'bg-component-input-button-container-color-background-secondary-enabled disabled:bg-component-input-button-container-color-background-secondary-disabled hover:bg-component-input-button-container-color-background-secondary-hover focus:bg-component-input-button-container-color-background-secondary-focused active:bg-component-input-button-container-color-background-secondary-pressed',
   tertiary:
      'bg-component-input-button-container-color-background-tertiary-enabled disabled:bg-component-input-button-container-color-background-tertiary-disabled hover:bg-component-input-button-container-color-background-tertiary-hover focus:bg-component-input-button-container-color-background-tertiary-focused active:bg-component-input-button-container-color-background-tertiary-pressed',
   quaternary:
      'bg-component-input-button-container-color-background-ghost-enabled disabled:bg-component-input-button-container-color-background-ghost-disabled hover:bg-component-input-button-container-color-background-ghost-hover focus:bg-component-input-button-container-color-background-ghost-focused active:bg-component-input-button-container-color-background-ghost-pressed',
   error: 'bg-component-input-button-container-color-background-danger-enabled disabled:bg-component-input-button-container-color-background-danger-disabled hover:bg-component-input-button-container-color-background-danger-hover focus:bg-component-input-button-container-color-background-danger-focused active:bg-component-input-button-container-color-background-danger-pressed',
   ghost: 'bg-component-input-button-container-color-background-ghost-enabled disabled:bg-component-input-button-container-color-background-ghost-disabled hover:bg-component-input-button-container-color-background-ghost-hover focus:bg-component-input-button-container-color-background-ghost-focused active:bg-component-input-button-container-color-background-ghost-pressed'
};

const textColors = {
   primary: 'enabled:text-component-input-button-label-color-text-primary-enabled disabled:text-component-input-button-label-color-text-primary-disabled',
   secondary: 'enabled:text-component-input-button-label-color-text-secondary-enabled disabled:text-component-input-button-label-color-text-secondary-disabled',
   tertiary: 'enabled:text-component-input-button-label-color-text-tertiary-enabled disabled:text-component-input-button-label-color-text-tertiary-disabled',
   quaternary: 'enabled:text-component-input-button-label-color-text-ghost-enabled disabled:text-component-input-button-label-color-text-ghost-disabled',
   error: 'enabled:text-component-input-button-label-color-text-danger-enabled disabled:text-component-input-button-label-color-text-danger-disabled',
   ghost: 'enabled:text-component-input-button-label-color-text-ghost-enabled disabled:text-component-input-button-label-color-text-ghost-disabled'
};

const borders = {
   primary: '',
   secondary: 'border-component-input-button-container-color-border-secondary-enabled disabled:border-component-input-button-container-color-border-secondary-disabled border-component-input-button-container-border-width-secondary',
   tertiary: '',
   quaternary: 'border-component-input-button-container-color-border-ghost-enabled disabled:border-component-input-button-container-color-border-ghost-disabled border-component-input-button-container-border-width-secondary',
   error: '',
   ghost: 'border-component-input-button-container-color-border-ghost-enabled disabled:border-component-input-button-container-color-border-ghost-disabled border-component-input-button-container-border-width-secondary'
};

export const Button: FC<Props> = ({ id, variant = 'primary', leadingIcon, trailingIcon, badge, disabled = false, type = 'button', form, fullWidth = false, ariaLabel, attributes = {}, onClick, children }) => {
   /* istanbul ignore next */
   const handleClick = () => !disabled && onClick?.();

   return (
      <button
         id={id}
         type={type}
         form={form}
         disabled={disabled}
         onClick={handleClick}
         aria-label={ariaLabel}
         {...dataAttributes(attributes)}
         className={classNames(
            'max-w-full min-h-component-input-button-container-sizing-min-height rounded-component-input-button-container-border-radius py-component-input-button-container-spacing-padding-y px-component-input-button-container-spacing-padding-x-no-icon',
            backgroundColors[variant],
            textColors[variant],
            borders[variant],
            {
               'pr-component-input-button-container-spacing-padding-x-no-icon': trailingIcon,
               'w-full': fullWidth
            }
         )}
      >
         <div className="gap-component-input-button-container-spacing-gap flex items-center justify-center break-words [&>span]:min-w-0">
            {leadingIcon && (
               <div className="shrink-0" data-testid="button--leading-icon">
                  <Icon icon={leadingIcon} />
               </div>
            )}
            <Typography variant="body-highlight" color="inherit" align="center">
               {children}
            </Typography>
            {badge && (
               <div className="shrink-0" data-testid="button-badge">
                  <Badge variant={disabled ? 'disabled' : badgeVariants[variant]}>{badge}</Badge>
               </div>
            )}
            {trailingIcon && (
               <div className="shrink-0" data-testid="button--trailing-icon">
                  <Icon icon={trailingIcon} />
               </div>
            )}
         </div>
      </button>
   );
};
